import { Controller } from "@hotwired/stimulus"

// Controller for managing a single organization's donation card
// Used in multi-organization Giving Day donations
export default class extends Controller {
  static targets = [
    "preset",
    "customAmount",
    "recurringCheckbox",
    "recurringOptions",
    "frequency",
    "endType",
    "endDateRow",
    "endDate",
    "tributeCheckbox",
    "tributeSection",
    "tributeText"
  ]

  static values = {
    orgId: String,
    itemId: String,
    cartId: String,
    isPrimary: Boolean
  }

  connect() {
    console.log("Org Donation Controller connected for org:", this.orgIdValue)
    this.donationAmount = 25 // Default amount

    // Initialize from selected preset
    const selectedPreset = this.presetTargets.find(target => target.checked)
    if (selectedPreset) {
      this.donationAmount = parseFloat(selectedPreset.value)
    } else if (this.hasCustomAmountTarget && this.customAmountTarget.value) {
      // Initialize from pre-populated custom amount (e.g., from donation level URL param)
      const customVal = parseFloat(this.customAmountTarget.value)
      if (!isNaN(customVal) && customVal > 0) {
        this.donationAmount = customVal
      }
    }

    // Update purchase summary on connect
    // Use setTimeout to ensure DOM is fully ready and other controllers are connected
    setTimeout(() => {
      this.updatePurchaseSummary()
    }, 100)
  }

  selectPreset(event) {
    this.donationAmount = parseFloat(event.currentTarget.value)

    // Clear custom amount
    if (this.hasCustomAmountTarget) {
      this.customAmountTarget.value = ''
    }

    this.updatePurchaseSummary()
    this.saveAmount()
  }

  clearPresets() {
    // Clear all preset selections when custom amount is focused
    this.presetTargets.forEach(preset => {
      preset.checked = false
    })
  }

  updateCustomAmount(event) {
    const value = event.currentTarget.value.trim()

    if (value) {
      const amount = parseFloat(value)

      if (isNaN(amount) || amount <= 0) {
        return
      }

      this.donationAmount = amount

      // Clear preset selections
      this.presetTargets.forEach(preset => {
        preset.checked = false
      })

      this.updatePurchaseSummary()
      this.debouncedSaveAmount()
    } else {
      this.donationAmount = 0
      this.updatePurchaseSummary()
    }
  }

  updatePurchaseSummary() {
    const baseAmount = this.donationAmount || 0
    console.log('updatePurchaseSummary called:', { baseAmount, orgId: this.orgIdValue })

    // Update per-organization donation amount in summary
    const orgAmountEl = document.querySelector(`[data-donation-amount-for-org="${this.orgIdValue}"]`)
    if (orgAmountEl) {
      orgAmountEl.textContent = `$${baseAmount.toFixed(2)}`
    }

    // Trigger refresh on the purchase-summary controller to recalculate totals
    // The purchase summary controller handles fee coverage calculation centrally
    const purchaseSummaryEl = document.querySelector('[data-controller~="purchase-summary"]')
    if (purchaseSummaryEl) {
      const purchaseSummaryController = this.application.getControllerForElementAndIdentifier(purchaseSummaryEl, 'purchase-summary')
      if (purchaseSummaryController) {
        purchaseSummaryController.refreshTotals()
      }
    }
  }

  toggleRecurring(event) {
    // Show/hide recurring options based on checkbox state
    if (this.hasRecurringOptionsTarget) {
      if (this.recurringCheckboxTarget.checked) {
        this.recurringOptionsTarget.classList.remove('d-none')
      } else {
        this.recurringOptionsTarget.classList.add('d-none')
      }
    }
    // Save recurring setting
    this.saveRecurring()
  }

  async saveRecurring() {
    const isRecurring = this.hasRecurringCheckboxTarget && this.recurringCheckboxTarget.checked
    const frequency = isRecurring && this.hasFrequencyTarget ? this.frequencyTarget.value : 'one_time'
    const endType = this.hasEndTypeTarget ? this.endTypeTarget.value : 'never'
    const endDate = (endType === 'date' && this.hasEndDateTarget) ? this.endDateTarget.value : null

    // Always update the UI display
    this.updateRecurringDisplay(isRecurring, frequency, endDate)

    // If no item ID, create the donation first
    if (!this.itemIdValue) {
      await this.createDonationItem()
    }

    // Still no item ID? Can't save
    if (!this.itemIdValue) return

    try {
      const response = await fetch(this.apiUrl(`/api/v1/checkout_donations/${this.itemIdValue}`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          frequency: frequency,
          end_type: endType,
          end_date: endDate
        })
      })

      const data = await response.json()

      // Dispatch event for any listeners
      if (data.donation) {
        this.dispatch("recurringUpdated", {
          detail: {
            itemId: this.itemIdValue,
            donation: data.donation,
            isRecurring: isRecurring,
            frequency: frequency
          }
        })
      }
    } catch (error) {
      console.error("Error saving recurring setting:", error)
    }
  }

  async createDonationItem() {
    if (!this.orgIdValue || !this.cartIdValue) return

    const amount = this.donationAmount || 25

    try {
      const response = await fetch(this.apiUrl('/api/v1/checkout_donations'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: this.orgIdValue,
          amount: amount
        })
      })

      const data = await response.json()

      if (data.donation && data.donation.id) {
        // Update the item ID value so future saves work
        this.itemIdValue = data.donation.id.toString()
        // Also update the DOM attribute
        this.element.dataset.orgDonationItemIdValue = data.donation.id.toString()
      }
    } catch (error) {
      console.error("Error creating donation item:", error)
    }
  }

  updateRecurringDisplay(isRecurring, frequency, endDate) {
    // Find the donation row in the purchase summary for this org
    const donationRow = document.querySelector(`[data-donation-org-id="${this.orgIdValue}"]`)
    if (!donationRow) return

    const donationLabel = donationRow.querySelector('.donation-label')
    if (!donationLabel) return

    // Remove existing recurring info spans
    const existingRecurringSpans = donationLabel.querySelectorAll('.recurring-info')
    existingRecurringSpans.forEach(span => span.remove())

    // Add new recurring info if recurring is enabled
    if (isRecurring && frequency && frequency !== 'one_time') {
      const frequencySpan = document.createElement('span')
      frequencySpan.className = 'ms-1 recurring-info'
      frequencySpan.textContent = `- ${this.titleize(frequency)}`
      donationLabel.appendChild(frequencySpan)

      if (endDate) {
        const endDateSpan = document.createElement('span')
        endDateSpan.className = 'ms-1 recurring-info'
        const formattedDate = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        endDateSpan.textContent = `until ${formattedDate}`
        donationLabel.appendChild(endDateSpan)
      }
    }
  }

  titleize(str) {
    return str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
  }

  toggleEndDate(event) {
    // Show/hide end date row based on end type selection
    if (this.hasEndDateRowTarget && this.hasEndTypeTarget) {
      if (this.endTypeTarget.value === 'date') {
        this.endDateRowTarget.classList.remove('d-none')
        // Set default date to 1 year from today if no date is set
        if (this.hasEndDateTarget && !this.endDateTarget.value) {
          const oneYearFromNow = new Date()
          oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)
          this.endDateTarget.value = oneYearFromNow.toISOString().split('T')[0]
        }
      } else {
        this.endDateRowTarget.classList.add('d-none')
        // Clear the date if switching to "never"
        if (this.hasEndDateTarget) {
          this.endDateTarget.value = ''
        }
      }
    }
    // Save the setting
    this.saveRecurring()
  }

  toggleTribute(event) {
    // Show/hide tribute section based on checkbox state
    if (this.hasTributeSectionTarget) {
      if (this.tributeCheckboxTarget.checked) {
        this.tributeSectionTarget.classList.remove('d-none')
      } else {
        this.tributeSectionTarget.classList.add('d-none')
      }
    }
  }

  async removeDonation(event) {
    event.preventDefault()

    if (this.isPrimaryValue) {
      // Can't remove primary organization
      console.warn("Cannot remove primary organization donation")
      return
    }

    try {
      const response = await fetch(this.apiUrl(`/api/v1/checkout_donations/${this.itemIdValue}`), {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        // Dispatch event to update Purchase Summary BEFORE removing element
        // (event needs to bubble from the element)
        this.dispatch("donationRemoved", {
          detail: {
            itemId: this.itemIdValue,
            orgId: this.orgIdValue,
            cartTotal: data.cart_total
          }
        })

        // Remove the card from DOM after dispatching event
        this.element.remove()
      }
    } catch (error) {
      console.error("Error removing donation:", error)
    }
  }

  debouncedSaveAmount() {
    clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => {
      this.saveAmount()
    }, 500)
  }

  async saveAmount() {
    // If no item ID, create the donation first
    if (!this.itemIdValue) {
      await this.createDonationItem()
    }

    // Still no item ID? Can't save
    if (!this.itemIdValue) return

    try {
      const requestBody = {
        amount: this.donationAmount
      }

      const response = await fetch(this.apiUrl(`/api/v1/checkout_donations/${this.itemIdValue}`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      if (data.donation) {
        // Dispatch event to update Purchase Summary
        this.dispatch("donationUpdated", {
          detail: {
            itemId: this.itemIdValue,
            donation: data.donation,
            cartTotal: data.cart_total
          }
        })
      }
    } catch (error) {
      console.error("Error saving donation amount:", error)
    }
  }

  // Build API URL with cart_id parameter
  apiUrl(path) {
    const url = new URL(path, window.location.origin)
    if (this.cartIdValue) {
      url.searchParams.set('cart_id', this.cartIdValue)
    }
    return url.toString()
  }
}
